import React, { useState, useEffect } from 'react';

// COMPONENTS
import CountdownComponent from '../countdownComponent';

// HOOKS
import { useSocket } from 'hooks/useSocket';

// CONTEXT
import { useSeasonTheme } from 'context/SeasonThemeContext';

// UTILS
import iconMap from 'utils/iconMap';

// ASSETS
import batSvg from 'assets/images/themes/halloween/bat.svg';

const CountdownTile = ({ title, event, isEditing = false, isViewing = false })=> {
    const { socket } = useSocket();
    const { seasonTheme } = useSeasonTheme();
    const [events, setEvents] = useState([]);

    const EmptyCalendarIcon = iconMap.emptyCalendar;

    // Gestion du chargement initial des événements depuis la BDD
    useEffect(() => {
        if (event) {
            try {
                let parsedEvents;
                if (typeof event === 'string') {
                    parsedEvents = JSON.parse(event);
                } else if (Array.isArray(event)) {
                    parsedEvents = event;
                } else {
                    parsedEvents = [event];
                }

                const transformedEvents = parsedEvents.map(e => ({
                    id: String(e.EVT_ID),
                    title: e.EVT_TITLE,
                    description: e.EVT_DESCRIPTION,
                    startDate: e.EVT_START_DATE,
                    endDate: e.EVT_END_DATE,
                    link: e.EVT_LINK
                }));
                // Trier les événements par date de début
                const sortedEvents = transformedEvents.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
                setEvents(sortedEvents);
            } catch (error) {
                console.error('Failed to parse event:', error);
            }
        }
    }, [event]);

    // Gestion des mises à jour en temps réel via websocket
    useEffect(() => {
        if (!socket) return;

        const handleEventUpdate = (data) => {
            if (data.type === 'created' || data.type === 'updated') {
                setEvents(prevEvents => {
                    // Comparer les id en string pour éviter les problèmes de type
                    const existingEventIndex = prevEvents.findIndex(e => {
                        const match = String(e.id) === String(data.event.id);
                        return match;
                    });
                    const newEvents = [...prevEvents];
                    if (existingEventIndex !== -1) {
                        newEvents[existingEventIndex] = {
                            ...newEvents[existingEventIndex],
                            ...data.event,
                            id: String(data.event.id),
                        };
                    } else {
                        newEvents.push({ ...data.event, id: String(data.event.id) });
                    }
                    return newEvents.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
                });
            } else if (data.type === 'deleted') {
                setEvents(prevEvents => {
                    const filtered = prevEvents.filter(e => String(e.id) !== String(data.eventId));
                    return filtered;
                });
            }
        };

        socket.on('event-updated', handleEventUpdate);

        return () => {
            socket.off('event-updated', handleEventUpdate);
        };
    }, [socket]);

    const now = Date.now();
    const filteredEvents = events.filter(e => {
        const eventStartDate = new Date(e.startDate).getTime();
        const eventEndDate = e.endDate ? new Date(e.endDate).getTime() : null;
        return eventEndDate ? (eventEndDate >= now) : (eventStartDate >= now);
    });

    return (
        <div className="p-4 rounded-lg tile-animate flex flex-col h-full overflow-hidden w-full relative">
            {/* Chauves-souris pour Halloween */}
            {seasonTheme === 'halloween' && (
                <>
                    <div className="absolute top-1 right-4 size-12">
                        <img src={batSvg} alt="Chauve-souris" className="w-full h-full" />
                    </div>
                </>
            )}
            
            <h3 className="text-lg md:text-xl lg:text-lg poppins-bold text-center text-blanc">{title}</h3>
            {filteredEvents.length > 0 ? (
                <div className="p-1 rounded-lg overflow-y-auto flex-grow relative"> 
                    {filteredEvents.map((e) => (
                        <CountdownComponent 
                            key={e.id} 
                            title={e.title} 
                            info={e.description} 
                            date={e.startDate}
                            endDate={e.endDate}
                            link={e.link}
                            onFinish={() => {
                                setEvents(prev => prev.filter(ev => ev.id !== e.id));
                            }}
                        />
                    ))}
                </div>
            ) : (
                <div className="px-2 lg:px-8 xl:px-12 flex flex-col justify-center items-center h-full">
                    <EmptyCalendarIcon className="size-14 lg:size-16 xl:size-14 2xl:size-18 fill-blanc" />
                    <p className="text-sm xl:text-lg mt-6 lg:mt-4 xl:mt-2 2xl:mt-6 text-center text-blanc">Aucun évènement en approche</p>
                </div>
            )}
        </div>
    );
};

export default CountdownTile;